/**
 * RankedBarChart renders a horizontal bar chart showing items ranked by value.
 * Sorted descending. Uses VictoryBar with Meridian styling.
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { VictoryBar, VictoryChart, VictoryAxis } from 'victory';
import { colorBlue400 } from '@amzn/meridian-tokens/base/color';
import { skeletonBase } from 'src/components/entity-metrics/skeleton';
import { useTranslation } from 'src/arb-wrapper';

interface RankedBarChartProps {
  data: Array<{ label: string; value: number }>;
  title: string;
  isLoading?: boolean;
}

export const RankedBarChart: React.FC<RankedBarChartProps> = ({
  data,
  title,
  isLoading,
}) => {
  const { t } = useTranslation();
  const sorted = [...data].sort((a, b) => b.value - a.value).slice(0, 10);
  const chartHeight = 180;

  return (
    <Column
      backgroundColor="white"
      spacing="300"
      spacingInset="400"
      type="outline"
    >
      <Heading level={4}>{title} {t('chart_top_10_suffix')}</Heading>
      {isLoading
        ? (
        <Column spacing="200">
          {[100, 80, 65, 50, 35].map((w, i) => (
            <Row key={i} spacing="200">
              <div
                style={{
                  width: 30,
                  height: 14,
                  borderRadius: 4,
                  background: '#f0f0f0',
                }}
              />
              <div
                style={{
                  ...skeletonBase,
                  width: `${w}%`,
                  height: 14,
                }}
              />
            </Row>
          ))}
        </Column>
          )
        : sorted.length === 0
          ? (
        <Text color="secondary">{t('chart_no_data_available')}</Text>
            )
          : (
        <VictoryChart
          domainPadding={{ x: 20 }}
          height={chartHeight}
          padding={{ top: 10, bottom: 30, left: 50, right: 40 }}
        >
          <VictoryAxis style={{ tickLabels: { fontSize: 10 } }} />
          <VictoryAxis dependentAxis style={{ tickLabels: { fontSize: 10 } }} />
          <VictoryBar
            data={sorted.map((d) => ({ x: d.label, y: d.value }))}
            horizontal
            labels={({ datum }) => String(datum.y)}
            style={{
              data: { fill: colorBlue400 },
              labels: { fontSize: 9 },
            }}
          />
        </VictoryChart>
            )}
    </Column>
  );
};
